import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';

import FilterContainer from './components/FilterContainer';
import NoticeCard from './components/NoticeCard';
import { useNoticeHooks } from './hooks/useNoticeHooks';
import { ICON_CLOSE } from 'assets/icons';
import Column from 'components/common/Column';
import CommonImage from 'components/common/CommonImage';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import TotalCountContainer from 'containers/QnaList/components/TotalCountContainer';
import PaginationContainer from 'containers/QnaList/components/PaginationContainer';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import SearchForm from 'components/common/SearchForm';
import Divider from 'components/Divider';
import HackleManager from 'utils/hackle';
import { MAX_WIDTH } from 'App';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import ContentsTopBar from 'components/common/ContentsTopBar';
import WrapperContainer from 'components/common/Wrapper';

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${props => (props.$isMobile ? '0 0 90px' : '0 0 40px')};
  width: 100%;
  max-width: 768px;
  min-height: 100vh;
  background-color: #fff;
`;

const NoticePage = () => {
  const {
    noticeBoard,
    noticeFilter,
    isOpenCountFilter,
    isAcceptFilter,
    handleSetFilterItem,
    handleIsOpenCountFilter,
  } = useNoticeHooks();

  const { width, ref } = useResizeDetector();

  const isMobile = width ? width <= MAX_WIDTH : window.innerWidth <= MAX_WIDTH;

  const [isOpenSearchModal, setIsOpenSearchModal] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(
    noticeFilter.filter.titleLike,
  );

  const handleOpenSearchModal = () => {
    setIsOpenSearchModal(true);
  };

  const handleCloseSearchModal = () => {
    if (noticeFilter.filter.titleLike.trim() === '') {
      setIsOpenSearchModal(false);
    } else {
      setSearchValue(noticeFilter.filter.titleLike);
      setIsOpenSearchModal(false);
    }
  };

  useEffect(() => {
    HackleManager.logNoticeview();
  }, []);

  const renderSearchModalContainer = (): JSX.Element => {
    return (
      <ModalContainer
        isMobile={isMobile}
        isAbled={true}
        onAcceptText="적용"
        onCloseText="초기화"
        onClick={() => {
          handleSetFilterItem('filter', {
            ...noticeFilter.filter,
            titleLike: searchValue,
          });
          setIsOpenSearchModal(false);
        }}
        onClose={() => {
          setSearchValue('');
        }}
        style={{ gap: 0 }}
      >
        <Row style={{ padding: '16px 0' }}>
          <CommonImage
            src={ICON_CLOSE}
            style={{ position: 'absolute', left: '20px' }}
            onClick={handleCloseSearchModal}
          />
          <Text color="#000" size="16px" weight={600}>
            검색
          </Text>
        </Row>
        <Column
          align="flex-start"
          gap="12px"
          style={{ padding: '12px 20px 32px' }}
        >
          <Text color="#1b1c1e" size="16px">
            검색어
          </Text>
          <SearchForm
            placeholder="공지사항 제목을 입력해 주세요."
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
        </Column>
      </ModalContainer>
    );
  };

  return (
    <WrapperContainer isMobile={isMobile}>
      <Container $isMobile={isMobile} ref={ref}>
        <ContentsTopBar
          isMobile={isMobile}
          title="공지사항"
          isAcceptFilter={isAcceptFilter}
          isShowFilter={true}
          onClick={handleOpenSearchModal}
        />
        <Column
          align="flex-start"
          style={{ padding: isMobile ? '0 20px' : '0 40px' }}
        >
          <FilterContainer
            noticeFilter={noticeFilter}
            handleSetFilterItem={handleSetFilterItem}
          />
        </Column>
        {noticeBoard && (
          <Column gap="32px" style={{ marginTop: '24px' }}>
            <Column style={{ padding: isMobile ? '0 20px' : '0 40px' }}>
              <TotalCountContainer
                totalCount={noticeBoard.board.notices.totalCount}
                isOpenCountFilter={isOpenCountFilter}
                filterItem={noticeFilter}
                handleSetFilterItem={handleSetFilterItem}
                handleIsOpenCountFilter={handleIsOpenCountFilter}
              />
            </Column>
            <Column
              gap="16px"
              style={{ padding: isMobile ? '0 20px' : '0 40px' }}
            >
              {noticeBoard.board.notices.totalCount === 0 ? (
                <EmptySettlement textContent="조회되는 내역이 없어요" />
              ) : (
                noticeBoard.board.notices.edges.map((item, idx) => {
                  return (
                    <React.Fragment key={idx}>
                      <NoticeCard item={item} type="list" />
                      {noticeBoard.board.notices.edges.length !== idx + 1 && (
                        <Divider />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </Column>
            <PaginationContainer
              totalCount={noticeBoard.board.notices.totalCount}
              page={noticeFilter.page}
              size={noticeFilter.size}
              handleSetQnaFilter={handleSetFilterItem}
            />
          </Column>
        )}
        {isOpenSearchModal && renderSearchModalContainer()}
      </Container>
    </WrapperContainer>
  );
};

export default NoticePage;
